import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, get } from '../../../lib/api';
import { useNotification } from '../../../shared/Notification/useNotification';
import {
  getDefaultNotificationValues,
  hasNotificationChanges,
  createNotificationPayload,
} from './utils/notificationPreferenceUtils';

const updateNotifications = data => {
  return api({
    method: 'patch',
    url: '/api/account_settings/update_notifications',
    data,
  });
};

export default function useNotificationPreferences() {
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();
  const { t } = useTranslation(['shared', 'accountSettings']);

  const { data: accountSettings = {}, isLoading } = useQuery(
    ['accountSettings'],
    () => get('/api/account_settings'),
  );

  const [formValues, setFormValues] = useState(() =>
    getDefaultNotificationValues({}),
  );

  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Only set form values when we have actual data from the API
    if (!isLoading && Object.keys(accountSettings).length > 0) {
      setFormValues(getDefaultNotificationValues(accountSettings));
      isInitializedRef.current = true;
    }
  }, [accountSettings, isLoading]);

  const { mutate } = useMutation(updateNotifications, {
    onMutate: () => {
      setIsSaving(true);
    },
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['accountSettings'], data);
      setIsSaving(false);
      showSuccess({
        content: t('accountSettings:notificationPreferences.success'),
        title: t('accountSettings:notificationPreferences.title'),
      });
    },
    onError: () => {
      setIsSaving(false);
    },
  });

  // Debounced auto-save function
  const debouncedSave = useCallback(
    newValues => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        // Only save if we have changes and the component is initialized
        if (
          isInitializedRef.current &&
          hasNotificationChanges(newValues, accountSettings)
        ) {
          const payload = createNotificationPayload(newValues);
          mutate(payload);
        }
      }, 800); // 800ms delay
    },
    [accountSettings, mutate],
  );

  const handleChange = e => {
    const { name, checked } = e.target;
    const newValues = {
      ...formValues,
      [name]: checked,
    };

    setFormValues(newValues);

    // Trigger auto-save only if component is initialized
    if (isInitializedRef.current) {
      debouncedSave(newValues);
    }
  };

  const handleSave = () => {
    const payload = createNotificationPayload(formValues);
    mutate(payload);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const hasChanges = hasNotificationChanges(formValues, accountSettings);

  return {
    values: formValues,
    handleChange,
    handleSave,
    hasChanges,
    isLoading,
    isSaving,
  };
}
